// site.js — shared chrome behavior (C1).
// Vanilla ES module, no framework. Progressive enhancement: the chrome is fully
// readable without JS; this only powers the mobile utility-menu disclosure and (FB66)
// the footer's policy drawer.

import { createFocusTrap } from './modules/focus-trap.js';
import { initBusy } from './modules/busy.js';
import { initChatWidget } from './modules/chat-widget.js';
import { announce } from './modules/announce.js';
import { initAnalytics, track } from './modules/analytics.js';
import { initVenuePopover } from './modules/venue-pop.js';

/**
 * Wire the subhead utility nav's mobile "Menu" disclosure button.
 * Mirrors the prototype's `.util--open` toggle, with the a11y the prototype lacked:
 * aria-expanded stays in sync, Escape closes and restores focus, and an outside
 * click dismisses the menu.
 */
function initUtilNav() {
  const nav = document.querySelector('.util');
  const toggle = nav?.querySelector('.util-toggle');
  if (!nav || !toggle) return;

  const setOpen = (open) => {
    nav.classList.toggle('util--open', open);
    toggle.setAttribute('aria-expanded', String(open));
  };

  toggle.addEventListener('click', (e) => {
    e.stopPropagation();
    setOpen(!nav.classList.contains('util--open'));
  });

  document.addEventListener('click', (e) => {
    if (nav.classList.contains('util--open') && !nav.contains(e.target)) setOpen(false);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && nav.classList.contains('util--open')) {
      setOpen(false);
      toggle.focus();
    }
  });
}

// FB20 · the promo tag icon discloses the offer note (old-site parity): one small anchored
// popover with a close X; Esc/outside-click close, focus returns to the tag.
function initOfferPopovers() {
  let pop = null;
  let trigger = null;
  const close = () => {
    if (!pop) return;
    pop.remove();
    pop = null;
    trigger?.focus();
    trigger = null;
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-offer]');
    if (!btn) {
      if (pop && !e.composedPath().includes(pop)) close();
      return;
    }
    if (pop && trigger === btn) { close(); return; }
    close();
    trigger = btn;
    pop = document.createElement('div');
    pop.className = 'offer-pop';
    pop.setAttribute('role', 'dialog');
    pop.setAttribute('aria-label', 'Promotional rate');
    const text = document.createElement('span');
    // Promo three-state (2026-10-01): data-offer="nights" marks a tag on a plain price whose
    // discount sits on other nights of the stay. The legacy copy points at the nightly view, but
    // only say so when that control is really in the same block: grid cards on events that show
    // stay totals withhold "View rates & total" (the total is already on the card), and a
    // pointer to a link that isn't there is worse than none.
    const ratesLink = btn.closest('[data-room-card], .hd-book-col, [data-hotel-id]')
      ?.querySelector('.n-rates-link');
    const seeRates = ratesLink ? ' See “View rates & total” for details.' : '';
    // Provenance (2026-10-01): the server puts the room type + night(s) the promo figure is good
    // on into data-offer-*; the sentence is built from them. A hotel-level tag (grid / detail
    // card) names the room type; a room-card tag has none (the room is the card). No nights →
    // the generic legacy copy.
    // data-offer-requires names the activation night(s) the stay must include for the promo to
    // apply at all (e.g. an "Extended Stay Discount" that needs Friday 4/2); the server leaves it
    // empty when those nights are already among the discounted ones.
    const { offer, offerRoom: room, offerNights: nights, offerRequires: requires } = btn.dataset;
    // A hotel-level tag (data-offer-room present) says "on select rooms" rather than naming the
    // room type (user 2026-10-02): the names are long and the room page tags the exact room.
    // One sentence (user 2026-10-01, "keep simple"): the condition when there is one, else the
    // night(s) the figure is good on; the hotel-level tag adds the room type.
    //   grid/detail: "Promo rate applied on select rooms because your stay includes Fri, Apr 2."
    //   room card:   "Promo rate applied because your stay includes Fri, Apr 2." / "… applied on Wed, Dec 11."
    // Near-miss (2026-10-02), data-offer="unlock": no promo for THIS stay, but including one
    // adjacent night would activate it. Names the date, never a price; the link reloads this
    // page with the stay grown by that night (a server-built relative query — nothing else is
    // accepted as an href).
    let copy;
    let changeDates = null;
    if (offer === 'unlock') {
      copy = `Promo rate available${room ? ' on select rooms' : ''} when your stay includes ${requires}.`;
      const href = btn.dataset.offerHref;
      if (href && href.startsWith('?')) {
        changeDates = document.createElement('a');
        changeDates.href = href;
        // Not "Change dates": the room page already has a button by that name (the date
        // picker), and two controls sharing a name must do the same thing.
        changeDates.textContent = `Add ${requires}`;
      }
    } else if (!nights) {
      copy = offer === 'nights'
        ? 'Promo rates apply on select nights of your stay.' + seeRates
        : 'Discounted rates apply only to specific nights';
    } else {
      // "applied", not "applies when": this tag only renders once the stay has met the condition.
      const condition = requires ? `because your stay includes ${requires}` : `on ${nights}`;
      copy = room
        ? `Promo rate applied on select rooms ${condition}.`
        : `Promo rate applied ${condition}.` + seeRates;
    }
    text.textContent = copy;
    if (changeDates) text.append(' ', changeDates);
    const x = document.createElement('button');
    x.type = 'button';
    x.className = 'tax-pop-x';
    x.setAttribute('aria-label', 'Close');
    x.innerHTML = '<i class="fa-solid fa-xmark" aria-hidden="true"></i>';
    x.addEventListener('click', close);
    pop.append(text, x);
    const host = btn.closest('.rateline, .rs-price-row, .view_rate') ?? btn.parentElement;
    host.style.position = host.style.position || 'relative';
    host.appendChild(pop);
    x.focus();
  });
  // CAPTURE phase + stopPropagation: the tag now also lives inside the rates drawers, whose own
  // Escape handlers run at bubble on document. The popover is the innermost layer, so one Escape
  // must close it alone and leave the drawer open (focus goes back to the tag, inside the drawer).
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && pop) {
      e.stopPropagation();
      close();
    }
  }, true);
}

// FB33/FB71 · navigation feedback now lives in modules/busy.js — see the ramp described there.

// FB66 · the footer's "Hotel Terms & Conditions" / "Privacy Policy" links open a shared drawer
// instead of navigating. The body is fetched from the page the link already points at
// (/{eventGuid}/policies?handler=Content) on FIRST open and cached — so no other page in the site
// pays anything for this, and with JS off the same links are ordinary navigations.
// Delegated (2026-10-12): the booking assistant renders [data-sitepol] links into its transcript
// long after load ("Read the full policies"), so the click is caught at the document, not bound
// per link. The chat panel stays open behind the drawer; closing the drawer returns focus to it.
function initPolicyDrawer() {
  const drawer = document.querySelector('[data-sitepol-drawer]');
  const backdrop = document.querySelector('[data-sitepol-backdrop]');
  const body = drawer?.querySelector('[data-sitepol-body]');
  const titleEl = drawer?.querySelector('[data-sitepol-title]');
  // ADA audit 2026-09-17: the body is no longer its own live region (it either went unannounced
  // — written while the drawer was aria-hidden — or read a whole Terms document aloud). One short
  // sentence into the drawer's empty status region instead, after the drawer is open.
  const status = drawer?.querySelector('[data-sitepol-status]');
  if (!drawer || !backdrop || !body) return;

  const trap = createFocusTrap(drawer);
  const cache = new Map();
  let isOpen = false;

  const close = () => {
    if (!isOpen) return;
    isOpen = false;
    trap.deactivate();                 // returns focus to the link that opened it
    drawer.classList.remove('open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');  // translated off-canvas only — aria-hidden alone
    backdrop.classList.remove('open'); // would leave its close button tabbable
    backdrop.hidden = true;
    document.body.style.overflow = '';
  };

  const open = (title) => {
    isOpen = true;
    titleEl.textContent = title;
    backdrop.hidden = false;
    backdrop.classList.add('open');
    drawer.classList.add('open');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    trap.activate();
    announce(status, `${title} loaded`);
  };

  // Capture phase: busy.js's document-level navigation feedback is registered first and runs in the
  // bubble phase, so this handler must cancel the click BEFORE it, or every drawer open would flash
  // the "Loading…" bar (it skips clicks that are already defaultPrevented).
  document.addEventListener('click', async (e) => {
    const link = e.target.closest?.('a[data-sitepol]');
    if (!link) return;
    // Leave real navigations alone: modified clicks and middle-clicks mean "new tab".
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    // MUST be synchronous: this handler awaits below, and preventDefault() after an await is
    // too late — the browser has already navigated. So we cancel first and navigate ourselves
    // if the fetch doesn't pan out.
    e.preventDefault();
    const kind = link.dataset.sitepol;
    // A link whose own words are an action ("Read the full policies") names the document instead.
    const title = link.dataset.sitepolTitle || link.textContent.trim();

    if (cache.has(kind)) {
      body.innerHTML = cache.get(kind);
      open(title);
      return;
    }

    const url = new URL(link.href, window.location.origin);
    // Delegation means ANY [data-sitepol] link reaches here, so the fetch target is checked, not
    // trusted: same origin and the policies route only. Anything else is left to navigate normally.
    if (url.origin !== window.location.origin || !/^\/[0-9a-f-]{36}\/policies$/i.test(url.pathname)) {
      window.location.href = link.href;
      return;
    }
    url.searchParams.set('handler', 'Content');
    let html;
    try {
      const res = await fetch(url, { headers: { 'X-Requested-With': 'fetch' } });
      if (!res.ok) throw new Error(String(res.status));
      // Server-rendered markup: terms are IHtmlSanitizer-sanitized server-side (same seam as
      // the checkout terms drawer and hotel-rates.js), privacy is static. Never raw user input.
      html = await res.text();
    } catch {
      // Failed fetch degrades to the real page the link already points at, not a dead click.
      document.dispatchEvent(new CustomEvent('ebk:busy', { detail: { label: 'Opening…' } }));
      window.location.href = link.href;
      return;
    }
    cache.set(kind, html);
    body.innerHTML = html;
    body.scrollTop = 0;
    open(title);
  }, true);

  drawer.querySelector('[data-sitepol-close]')?.addEventListener('click', close);
  backdrop.addEventListener('click', close);
  // preventDefault marks the Escape as used: the chat panel under the drawer skips a consumed Escape,
  // so one press closes the drawer only and focus returns to the chat link that opened it.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !isOpen) return;
    e.preventDefault();
    close();
  });
}

/**
 * ADA screen-reader review 2026-09-18: "Skip to main content goes quiet". The link's target is
 * <main tabindex="-1">, which has no accessible name, so focusing it gives a screen reader nothing
 * to say. Land on the page's heading instead (the h1 — sr-only on the hotel list, where it names
 * the event's hotels): a focused heading is always spoken, and reading continues from there.
 * Falls back to the native jump when main has no heading, or the heading can't take focus.
 */
function initSkipLink() {
  const link = document.querySelector('.skip-link');
  const main = document.getElementById('main');
  if (!link || !main) return;
  link.addEventListener('click', (e) => {
    const heading = main.querySelector('h1') ?? main.querySelector('h2');
    if (!heading) return;
    e.preventDefault();
    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    main.scrollIntoView({ block: 'start' }); // where the native #main jump would have scrolled
    heading.focus({ preventScroll: true });
    if (document.activeElement !== heading) main.focus({ preventScroll: true }); // e.g. display:none
  });
}

function init() {
  // G4: GTM dataLayer + Klaviyo events (page-load island + [data-track] clicks).
  initAnalytics();
  initSkipLink();
  initUtilNav();
  initOfferPopovers();
  initVenuePopover();
  initBusy();
  initPolicyDrawer();
  // Epic H placeholder (Q5); no-ops unless AiFeatures:Chat:Enabled rendered the widget.
  initChatWidget(document, { trackEvent: track });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
